import * as React from "react";
import { QuestionDTO } from "../../services/questions";
import { Answer as AnswerType } from "../../services/answers";
import { PageType, ChildProps } from "../main/main";
import QuestionComponent, { Question } from "../question/question";

import styles from "./result.module.scss";

import { ReactComponent as ArrowRight } from "../../style/assets/arrow-right.svg";

const Result = (props: ChildProps & { questions: QuestionDTO[] }) => {
    const { questions, setFooter, setPage } = props;

    React.useEffect(() => {
        window.onbeforeunload = () => undefined;

        setFooter({
            Button: undefined,
        });
    }, [setFooter]);

    const { correctAnswers, incorrectAnswers } = getAnswers();

    let titleCopy;
    let textCopy;
    switch (true) {
        case (correctAnswers.length === 15):
            titleCopy = "Well done! You're a Safety Superhero."
            textCopy = "Thanks for helping to keep yourself and others safe on our waterways."
            break;
        case (correctAnswers.length >= 10):
            titleCopy = "Well done! You're a Safety Champion."
            textCopy = "Be sure to brush up on your safety knowledge to keep yourself and others safe on our waterways."
            break;
        case (correctAnswers.length >= 8):
            titleCopy = "Good effort. You're a Safety Advocate."
            textCopy = "Keep revising your safety knowledge to keep yourself and others safe on our waterways."
            break;
        case (correctAnswers.length >= 5):
            titleCopy = "Thanks for trying! You're a Safety Apprentice."
            textCopy = "Be sure to study up on your safety knowledge to keep yourself and others safe on our waterways."
            break;
        default:
        case (correctAnswers.length >= 0):
            titleCopy = "Thanks for trying! You're a Safety Trainee."
            textCopy = "Spend some time learning the safety rules before you head out on the water."
            break;
    }

    return (
        <div>
            <div className={styles.title}>{titleCopy}</div>
            <div className={styles.subtitle}>
                You've answered {correctAnswers.length}/{questions.length} questions correct!
            </div>
            <div className={styles.text}>{textCopy}</div>
            <div className={styles.actionButton}>
                <button className={"quiz_button"} onClick={() => setPage(PageType.survey)}>
                    Take quiz again
                    <ArrowRight className={"arrow"} title="Go to quiz" />
                </button>
            </div>
            {incorrectAnswers.length > 0 && (
                <>
                    <div className={styles.incorrectRibbon}>
                        {`${incorrectAnswers.length} wrong answers`}
                    </div>
                    {incorrectAnswers.map(x => {
                        return (
                            <Answer key={x.questionId} answer={x} questions={questions} isCorrect={false} />
                        );
                    })}
                </>
            )}
            {correctAnswers.length > 0 && (
                <>
                    <div
                        className={styles.correctRibbon}
                    >{`${correctAnswers.length} correct answers`}</div>
                    {correctAnswers.map(x => {
                        return (
                            <Answer key={x.questionId} answer={x} questions={questions} isCorrect={true} />
                        );
                    })}
                </>
            )}
            <div className={styles.actionButton}>
                <button className={"quiz_button"} onClick={() => setPage(PageType.survey)}>
                    Take quiz again
                    <ArrowRight className={"arrow"} title="Go to quiz" />
                </button>
            </div>
        </div>
    );
};

const Answer = (props: { answer: AnswerType; questions: QuestionDTO[]; isCorrect: boolean }) => {
    const { answer, questions, isCorrect } = props;
    const q = questions.find(x => x.id === answer.questionId);
    if (!q) return null;
    const i = questions.indexOf(q);
    const question: Question = {
        id: q.id,
        options: q.options.map(x => x.option),
        style: "radio",
        correctOption: q.options.find(x => x.type === "Answer")?.option || undefined,
    };
    const selectedAnswer = q.options.find(x => x.type === answer.type)?.option;
    return (
        <div className={styles.answer}>
            <div className={styles.textContainer} data-correct={isCorrect}>
                <div className={styles.questionText}>
                    {`${i + 1}. ${q.question}`}
                </div>
            </div>
            {q.image && (
                <div className={styles.imageContainer}>
                    <img
                        className={styles.questionImage}
                        src={`data:image/jpeg;base64,${q.image}`}
                        alt="Question illustration"
                    />
                </div>
            )}
            <QuestionComponent
                question={question}
                selectedAnswer={selectedAnswer || ""}
                setAnswer={() => undefined}
                showCorrectOption={true}
                disabled={true}
            />
        </div>
    );
};

const getAnswers = () => {
    const json = sessionStorage.getItem("answers");
    const answers = json ? JSON.parse(json) : undefined;
    const correctAnswers: AnswerType[] = answers.filter((x: AnswerType) => x.type === "Answer");
    const incorrectAnswers: AnswerType[] = answers.filter((x: AnswerType) => x.type !== "Answer");
    return {
        correctAnswers,
        incorrectAnswers,
    };
};

export default Result;
